import { secureStorage } from '../../utils/secureStorage';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ISO_CURRENCY_CODE = /^[A-Z]{3}$/;

export function normalizeCurrencyCode(currencyCode) {
  const code = String(currencyCode || '').trim().toUpperCase();
  return ISO_CURRENCY_CODE.test(code) ? code : '';
}

function formatPlainAmount(amount) {
  return new Intl.NumberFormat('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function normalizeCurrencySymbol(symbol) {
  return String(symbol || '').trim();
}

/** API currencySymbol first, then Intl for the ISO code. */
export function formatPurchaseAmount(amount, currencyCode, currencySymbol) {
  const code = normalizeCurrencyCode(currencyCode);
  const symbol = normalizeCurrencySymbol(currencySymbol);
  const value = Number(amount);
  const safeAmount = Number.isFinite(value) ? value : 0;
  const number = formatPlainAmount(safeAmount);

  if (symbol) return `${symbol}${number}`;
  if (!code) return number;

  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).format(safeAmount);
  } catch (error) {
    return `${number} ${code}`;
  }
}

/** Same as formatPurchaseAmount, with the ISO code when it is not already in the string. */
export function formatPurchaseAmountWithCode(amount, currencyCode, currencySymbol) {
  const code = normalizeCurrencyCode(currencyCode);
  const formatted = formatPurchaseAmount(amount, code, currencySymbol);
  if (!code || formatted.toUpperCase().includes(code)) return formatted;
  return `${formatted} ${code}`;
}

export function isValidPurchaseEmail(email) {
  return EMAIL_PATTERN.test(String(email || '').trim());
}

export function parsePurchaseQuantity(value) {
  const qty = parseInt(value, 10);
  if (!qty || qty < 1) return null;
  return qty;
}

export function getPurchaseLineTotal(plan, quantity) {
  return plan.price * quantity;
}

export const PURCHASE_SESSION_KEYS = {
  PLAN_ID: 'purchase.paypal.planId',
  EMAIL: 'purchase.paypal.email',
  QUANTITY: 'purchase.paypal.quantity',
  PLAN: 'purchase.paypal.plan',
};

function readStoredPlan(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
}

export function savePurchaseCheckoutSession({ planId, email, quantity, plan }) {
  sessionStorage.setItem(PURCHASE_SESSION_KEYS.PLAN_ID, planId);
  sessionStorage.setItem(PURCHASE_SESSION_KEYS.EMAIL, email);
  sessionStorage.setItem(PURCHASE_SESSION_KEYS.QUANTITY, String(quantity));
  if (plan) sessionStorage.setItem(PURCHASE_SESSION_KEYS.PLAN, JSON.stringify(plan));
}

export function readPurchaseCheckoutSession() {
  const planId = sessionStorage.getItem(PURCHASE_SESSION_KEYS.PLAN_ID);
  const email = sessionStorage.getItem(PURCHASE_SESSION_KEYS.EMAIL);
  const quantity = parsePurchaseQuantity(sessionStorage.getItem(PURCHASE_SESSION_KEYS.QUANTITY));
  const plan = readStoredPlan(sessionStorage.getItem(PURCHASE_SESSION_KEYS.PLAN));
  return { planId, email, quantity, plan };
}

export function clearPurchaseCheckoutSession() {
  Object.values(PURCHASE_SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
}

/** True when the PayPal return URL marks a pre-authenticated (signed-in) purchase. */
export function isPurchasePreAuth(search) {
  const raw = new URLSearchParams(search || '').get('preAuth');
  if (raw == null) return false;
  const value = String(raw).trim().toLowerCase();
  return value === 'true' || value === '1' || value === 'yes';
}

export function isPurchaseSignedIn() {
  return Boolean(secureStorage.getItem('token'));
}

/** Post-capture destination for signed-in (preAuth) buyers — plan dashboard. */
export const PURCHASE_SIGNED_IN_SUCCESS_PATH = '/dashboard';

/**
 * Builds the post-PayPal handoff URL into the promo/subscription-key onboarding flow (#3).
 * Prefills the first issued subscription key + purchaser email as an organisation user.
 */
export function buildPurchaseOnboardingRegistrationUrl({
  subscriptionKeys = [],
  email = '',
  userType = 2,
} = {}) {
  const params = new URLSearchParams();
  const subscriptionKey = Array.isArray(subscriptionKeys)
    ? String(subscriptionKeys.find((key) => String(key || '').trim()) || '').trim()
    : String(subscriptionKeys || '').trim();
  const normalizedEmail = String(email || '').trim();

  if (subscriptionKey) params.set('subscriptionKey', subscriptionKey);
  if (normalizedEmail) params.set('email', normalizedEmail);
  if (userType != null && userType !== '') params.set('usertype', String(userType));

  const search = params.toString();
  return search ? `/onboarding/registration?${search}` : '/onboarding/registration';
}
